import { FC } from 'react';
import { BodyText, Card, CircleButton, Icon, withStyles } from 'vopay';

import Dialog from '#shared/components/Dialog/Dialog';
import styles from './UpgradePlanDialog.module.scss';
import { useTranslation } from 'react-i18next';

import { Button, Title } from '#shared/components/UI';

type UpgradePlanDialogProps = {
  onCancel: () => void;
  // Captured from useHostNavigate() by the opener — the dialog renders under
  // the DialogProvider, above the HostNavigationProvider, so the context
  // can't be read here directly.
  hostNavigate?: (path: string, state?: unknown) => void;
};

const cn = withStyles(styles);

const UpgradePlanDialog: FC<UpgradePlanDialogProps> = ({ onCancel, hostNavigate }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.InteracVerification.UpgradePlanDialog' });

  return (
    <Card className={cn('modalContainer')}>
      <Dialog.Title level="3">{t('title', { defaultValue: 'Upgrade Your Plan!' })}</Dialog.Title>

      <div className={cn('closeButton')}>
        <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
      </div>

      <BodyText className={cn('description')}>
        {t('desc', {
          defaultValue: 'Confirm ownership and registration details retrieved from Interac for this contact.',
        })}
      </BodyText>

      <div className={cn('plans')}>
        <div className={cn('plan', 'basic')}>
          <Icon icon="checkBlueGradient" width="80px" height="80px" color="none" />

          <div>
            <Title level="5">{t('Basic.title', { defaultValue: 'Basic' })}</Title>

            <BodyText className={cn('text')}>
              {t('Basic.desc', { defaultValue: 'Verify the ownership of the Interac account.' })}
            </BodyText>
          </div>
        </div>

        <div className={cn('plan', 'enhanced')}>
          <Icon icon="iconsGradient" width="80px" height="80px" color="none" />

          <div>
            <Title className={cn('title')} level="5">
              {t('Enhanced.title', { defaultValue: 'Enhanced' })}
            </Title>

            <BodyText className={cn('text')}>
              {t('Enhanced.desc', {
                defaultValue: 'Confirm ownership and access registration details retrieved from Interac.',
              })}
            </BodyText>
          </div>
        </div>
      </div>

      <Button
        className={cn('button')}
        colorScheme="primary"
        onClick={() => {
          onCancel();

          // Link-out via the host's onNavigate; the dialog just closes when the
          // host can't navigate. (dropped router state: ticketSubject)
          hostNavigate?.('/create-tickets', { ticketSubject: 'Enquiry about Interac email verification' });
        }}
      >
        {t('contactButton', { defaultValue: 'Contact Support' })}
      </Button>
    </Card>
  );
};

export default UpgradePlanDialog;
